/* ============================================================
   NeuralLines — a faint web of nerve-like lines that drifts slowly,
   with small lights travelling along the lines like slow electricity.

   · Nodes sit around a focus point (by default: wherever the orb is) and
     thin curved lines fan out of them and link them together.
   · Everything sways very slowly. Lights run along the lines, fading in at
     one end and out at the other, so nothing ever pops or jumps.
   · The web is densest and brightest near the focus and dies away with
     distance, leaving the far side of the section empty for text.

   Canvas 2D — no libraries, nothing loaded from another domain.
   Transparent background: put it over the dark section background.

   USE
     1. Load this file once on the page (a normal deferred script tag).
     2. Put a canvas with the attribute  data-neural-lines  behind the orb,
        filling the section. Give it  display:block; width:100%; height:100%.
     3. To keep the lines out of the orb, point it at the orb's canvas:
          <canvas data-neural-lines data-avoid="#orb"></canvas>

     Options as attributes (data-nodes="20", data-pulse="0.6" …) or in code:
       var lines = NeuralLines(canvasElement, { avoid: '#orb' });
       lines.pause(); lines.play(); lines.destroy();
   ============================================================ */
(function (global) {
  'use strict';

  var DEFAULTS = {
    nodes: 17,             // number of nodes in the web
    focusX: 0.21,          // where the web gathers, as a fraction of the canvas width  (ignored when `avoid` is set: it follows that element)
    focusY: 0.47,          // … and of its height
    reach: 0.85,           // how far the web spreads (1 = it dies away about three quarters of a canvas-height from the focus)
    speed: 1,              // how fast the web sways (0 = still)
    pulse: 1,              // how fast the lights travel (1 = very slow · 0 = no lights)
    pulses: 0.3,           // share of the lines that carry a light
    brightness: 1,         // brightness of the lines
    glow: 1,               // brightness of the travelling lights
    energy: 0,             // 0 = calm · 1 = fully charged: brighter lines, more and faster lights, whole strands lighting up. Meant to be driven from code: lines.set('energy', 0.6)
    color: '164,150,234',  // line colour, as r,g,b
    light: '238,228,255',  // colour of the travelling lights, as r,g,b
    avoid: '',             // CSS selector of the orb's canvas: the lines stay out of it and gather around it
    avoidRadius: 0.28,     // radius kept clear, as a fraction of that element's shorter side (the orb's data-size)
    seed: 7                // any number: a different web
  };

  var TAU = Math.PI * 2;

  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function smooth(lo, hi, x) { var t = Math.min(1, Math.max(0, (x - lo) / (hi - lo))); return t * t * (3 - 2 * t); }

  function NeuralLines(canvas, options) {
    var cfg = {}, key;
    for (key in DEFAULTS) cfg[key] = options && options[key] !== undefined ? options[key] : DEFAULTS[key];
    var ctx = canvas.getContext('2d');
    if (!ctx) return { play: function () {}, pause: function () {}, advance: function () {}, set: function () {}, nodes: function () { return []; }, highlight: function () {}, destroy: function () {} };

    /* ---------- the web, in units of "one canvas-height", relative to the focus ---------- */
    var nodes = [], strands = [];
    (function build() {
      var rand = rng(Math.round(cfg.seed * 1000) + 11), r2 = rng(Math.round(cfg.seed * 1000) + 977), i, j, k;
      function sway() { return { a: 0.012 + rand() * 0.022, b: 0.012 + rand() * 0.022, w1: TAU / (34 + rand() * 40), w2: TAU / (34 + rand() * 40), p1: rand() * TAU, p2: rand() * TAU }; }

      /* nodes: a loose ring around the focus, thinning with distance */
      var n = Math.max(3, cfg.nodes | 0), tries = 0;
      while (nodes.length < n && tries++ < 4000) {
        var ang = rand() * TAU, r = 0.21 + Math.pow(rand(), 1.15) * 0.60;
        var x = Math.cos(ang) * r * 1.12, y = Math.sin(ang) * r * 0.9, ok = true;
        for (j = 0; j < nodes.length; j++) if (Math.hypot(nodes[j].x - x, nodes[j].y - y) < 0.13) { ok = false; break; }
        if (!ok) continue;
        var kind = rand();
        nodes.push({ x: x, y: y, d: Math.hypot(x, y), s: sway(), flash: 0,
                     kind: kind < 0.36 ? 'ring' : kind < 0.72 ? 'dot' : 'soft', size: 0.8 + rand() * 0.5 });
      }

      function add(o) {
        o.k1 = o.bias + (rand() - 0.5) * 2 * o.curve; o.k2 = -o.bias * 0.55 + (rand() - 0.5) * 2 * o.curve;   // strands of one fan bend alike, in a soft S
        o.wk = TAU / (40 + rand() * 50); o.pk = rand() * TAU;
        o.shade = 0.55 + rand() * 0.45;
        o.soft = rand() < 0.22;                                        // some are out of focus: wider and fainter
        var roll = rand();                                             // lines with a low roll carry a light from the start; the rest join in as the energy rises
        if (roll < cfg.pulses && !o.arc) o.pulse = { period: 11 + rand() * 13, phase: rand(), len: 0.16 + rand() * 0.12, dir: rand() < 0.7 ? 1 : -1, tw: rand() * 40, th: roll };
        else if (!o.arc) o.pulse = { period: 11 + r2() * 13, phase: r2(), len: 0.16 + r2() * 0.12, dir: r2() < 0.7 ? 1 : -1, tw: r2() * 40, th: roll };
        o.surge = { period: 30 + r2() * 50, phase: r2() };              // now and then a whole strand lights up
        strands.push(o);
      }

      /* tendrils: fans of fine lines leaving each node, ending in open space */
      nodes.forEach(function (nd, idx) {
        var fans = 1 + (rand() < 0.75 ? 1 : 0) + (rand() < 0.3 ? 1 : 0);
        for (i = 0; i < fans; i++) {
          var main = rand() * TAU, len = 0.13 + rand() * 0.27, count = 2 + Math.floor(rand() * 4), bend = (rand() < 0.5 ? -1 : 1) * (0.14 + rand() * 0.3);
          for (k = 0; k < count; k++) {
            var a2 = main + (rand() - 0.5) * 0.62, l2 = len * (0.65 + rand() * 0.7);
            add({ from: idx, ex: Math.cos(a2) * l2, ey: Math.sin(a2) * l2, s: sway(), curve: 0.07, bias: bend });
          }
        }
      });

      /* links: one to three strands between a node and its nearest neighbours */
      nodes.forEach(function (nd, idx) {
        var near = nodes.map(function (o, m) { return { m: m, d: Math.hypot(o.x - nd.x, o.y - nd.y) }; })
          .filter(function (o) { return o.m > idx && o.d < 0.5; }).sort(function (p, q) { return p.d - q.d; }).slice(0, 2);
        near.forEach(function (o) {
          var count = 1 + Math.floor(rand() * 3), bend = (rand() < 0.5 ? -1 : 1) * (0.12 + rand() * 0.3);
          for (k = 0; k < count; k++) add({ from: idx, to: o.m, curve: 0.09, bias: bend });
        });
      });

      /* long, very faint sweeps that cross the whole web */
      for (i = 0; i < 9; i++) {
        var a0 = rand() * TAU, a1 = a0 + 1.6 + rand() * 2.2, r0 = 0.55 + rand() * 0.4, r1 = 0.55 + rand() * 0.4;
        add({ arc: true, x0: Math.cos(a0) * r0 * 1.15, y0: Math.sin(a0) * r0, ex: Math.cos(a1) * r1 * 1.15, ey: Math.sin(a1) * r1,
              s: sway(), s0: sway(), curve: 0.12, bias: (rand() < 0.5 ? -1 : 1) * (0.2 + rand() * 0.3) });
      }
    })();

    strands.forEach(function (o) { if (o.arc) return; nodes[o.from].links = (nodes[o.from].links || 0) + 1; if (o.to !== undefined) nodes[o.to].links = (nodes[o.to].links || 0) + 1; });

    /* a soft point of light, drawn once and reused */
    var sprite = document.createElement('canvas'); sprite.width = sprite.height = 64;
    (function () {
      var g = sprite.getContext('2d'), gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      gr.addColorStop(0, 'rgba(' + cfg.light + ',1)'); gr.addColorStop(0.18, 'rgba(' + cfg.light + ',.55)');
      gr.addColorStop(0.5, 'rgba(' + cfg.color + ',.16)'); gr.addColorStop(1, 'rgba(' + cfg.color + ',0)');
      g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
    })();

    /* ---------- size ---------- */
    var W = 1, H = 1, DPR = 1, U = 1;
    function resize() {
      var box = canvas.getBoundingClientRect();
      W = Math.max(1, box.width); H = Math.max(1, box.height);
      DPR = Math.min(global.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
      U = Math.min(H, W * 1.25) * cfg.reach;                     // one unit of the web, in pixels
    }

    function pt(o, t) {                                            // a point on a strand's curve
      var m = 1 - t, a = m * m * m, b = 3 * m * m * t, c = 3 * m * t * t, d = t * t * t;
      return [a * o.X0 + b * o.X1 + c * o.X2 + d * o.X3, a * o.Y0 + b * o.Y1 + c * o.Y2 + d * o.Y3];
    }

    /* ---------- one frame ---------- */
    var avoidEl = null, time = cfg.seed * 13.7 + 20, ptime = cfg.seed * 3.1, stime = 0, fade = 0;
    function draw() {
      var fx = cfg.focusX * W, fy = cfg.focusY * H, hole = 0, i, o, n, e;
      if (cfg.avoid && !avoidEl) avoidEl = document.querySelector(cfg.avoid);
      if (avoidEl) {
        var a = avoidEl.getBoundingClientRect(), c = canvas.getBoundingClientRect();
        if (a.width > 0) { fx = a.left + a.width / 2 - c.left; fy = a.top + a.height / 2 - c.top; hole = Math.min(a.width, a.height) * cfg.avoidRadius; }
      }
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';

      /* where everything is right now */
      for (i = 0; i < nodes.length; i++) {
        n = nodes[i];
        n.X = fx + (n.x + n.s.a * Math.sin(time * n.s.w1 + n.s.p1)) * U;
        n.Y = fy + (n.y + n.s.b * Math.sin(time * n.s.w2 + n.s.p2)) * U;
        n.vis = (1 - smooth(0.36, 0.86, n.d)) * (0.75 + 0.5 * (1 - smooth(0.22, 0.5, n.d)));
        n.flash = 0;
        n.hi = (n.hi || 0) + ((n.hiTo || 0) - (n.hi || 0)) * 0.07;         // a node holding a label glows steadily (eased in and out)
      }
      var en = Math.max(0, Math.min(1.5, cfg.energy));                // how charged the web is
      var base = 0.27 * cfg.brightness * fade * (1 + 0.75 * en);

      /* the lines */
      for (i = 0; i < strands.length; i++) {
        o = strands[i];
        var v0, v1;
        if (o.arc) {
          o.X0 = fx + (o.x0 + o.s0.a * 2 * Math.sin(time * o.s0.w1 + o.s0.p1)) * U; o.Y0 = fy + (o.y0 + o.s0.b * 2 * Math.sin(time * o.s0.w2 + o.s0.p2)) * U;
          o.X3 = fx + (o.ex + o.s.a * 2 * Math.sin(time * o.s.w1 + o.s.p1)) * U;   o.Y3 = fy + (o.ey + o.s.b * 2 * Math.sin(time * o.s.w2 + o.s.p2)) * U;
          v0 = v1 = 0;
        } else {
          n = nodes[o.from]; o.X0 = n.X; o.Y0 = n.Y; v0 = n.vis;
          if (o.to !== undefined) { e = nodes[o.to]; o.X3 = e.X; o.Y3 = e.Y; v1 = e.vis; }
          else {
            o.X3 = n.X + (o.ex + o.s.a * 1.8 * Math.sin(time * o.s.w1 + o.s.p1)) * U;
            o.Y3 = n.Y + (o.ey + o.s.b * 1.8 * Math.sin(time * o.s.w2 + o.s.p2)) * U; v1 = 0;
          }
        }
        var dx = o.X3 - o.X0, dy = o.Y3 - o.Y0;
        var w = Math.sin(time * o.wk + o.pk) * 0.07;                  // the curve itself breathes
        var k1 = o.k1 + w, k2 = o.k2 - w;
        o.X1 = o.X0 + dx * 0.33 - dy * k1; o.Y1 = o.Y0 + dy * 0.33 + dx * k1;
        o.X2 = o.X0 + dx * 0.68 - dy * k2; o.Y2 = o.Y0 + dy * 0.68 + dx * k2;

        var g = ctx.createLinearGradient(o.X0, o.Y0, o.X3, o.Y3), al;
        if (o.arc) {
          var mx = (o.X0 + o.X3) / 2 - fx, my = (o.Y0 + o.Y3) / 2 - fy;
          al = base * 0.5 * o.shade * (1 - smooth(0.3, 0.8, Math.hypot(mx, my) / U));
          g.addColorStop(0, 'rgba(' + cfg.color + ',0)'); g.addColorStop(0.5, 'rgba(' + cfg.color + ',' + al.toFixed(4) + ')'); g.addColorStop(1, 'rgba(' + cfg.color + ',0)');
          o.vis = 0; o.v0 = o.v1 = 0;
        } else {
          al = base * o.shade;
          g.addColorStop(0, 'rgba(' + cfg.color + ',' + (al * v0).toFixed(4) + ')');
          g.addColorStop(1, 'rgba(' + cfg.color + ',' + (al * v1 * 0.9).toFixed(4) + ')');
          o.vis = Math.max(v0, v1); o.v0 = v0; o.v1 = v1;
        }
        ctx.strokeStyle = g; ctx.lineWidth = o.soft ? 2.6 : 1; ctx.globalAlpha = o.soft ? 0.5 : 1;
        ctx.beginPath(); ctx.moveTo(o.X0, o.Y0); ctx.bezierCurveTo(o.X1, o.Y1, o.X2, o.Y2, o.X3, o.Y3); ctx.stroke();
      }
      ctx.globalAlpha = 1;

      /* the lights travelling along them */
      if (cfg.pulse > 0) {
        ctx.globalCompositeOperation = 'lighter';
        for (i = 0; i < strands.length; i++) {
          o = strands[i]; var p = o.pulse;
          if (!p || o.vis < 0.02) continue;
          var on = smooth(0, 0.08, cfg.pulses + 0.45 * en - p.th);     // this line's light is switched on (softly) once the energy reaches it
          if (on <= 0) continue;
          var s = ptime / p.period + p.phase; s -= Math.floor(s);
          var env = Math.pow(Math.sin(Math.PI * s), 0.6);             // fades in at one end and out at the other
          var trem = 0.16 + 0.14 * Math.min(1, en);
          var flick = 1 - trem + trem * Math.sin(ptime * 7.3 + p.tw) * Math.sin(ptime * 4.1 + p.tw * 1.7);   // a slight electric tremble
          var head = p.dir > 0 ? s : 1 - s, tail = head - p.dir * p.len * (0.4 + 0.6 * env);
          tail = Math.min(1, Math.max(0, tail));
          var vv = Math.max(o.v0 + (o.v1 - o.v0) * head, 0.3 * o.vis);  // as bright as the line is at that point
          var strength = 0.7 * env * flick * vv * cfg.glow * fade * on * (1 + 0.8 * en);
          if (strength < 0.01) continue;
          var hp = pt(o, head), tp = pt(o, tail), q, m;
          var lg = ctx.createLinearGradient(tp[0], tp[1], hp[0], hp[1]);
          lg.addColorStop(0, 'rgba(' + cfg.light + ',0)'); lg.addColorStop(1, 'rgba(' + cfg.light + ',' + Math.min(1, strength * 0.7).toFixed(3) + ')');
          ctx.strokeStyle = lg; ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.moveTo(tp[0], tp[1]);
          for (m = 1; m <= 8; m++) { q = pt(o, tail + (head - tail) * m / 8); ctx.lineTo(q[0], q[1]); }
          ctx.stroke();
          var r = (5 + 3 * env) * (1 + 0.35 * en);
          ctx.globalAlpha = Math.min(1, strength); ctx.drawImage(sprite, hp[0] - r, hp[1] - r, r * 2, r * 2); ctx.globalAlpha = 1;
          /* a node brightens as a light leaves or arrives */
          if (head < 0.12) nodes[o.from].flash = Math.max(nodes[o.from].flash, (1 - head / 0.12) * env * 2.2);
          if (o.to !== undefined && head > 0.88) nodes[o.to].flash = Math.max(nodes[o.to].flash, ((head - 0.88) / 0.12) * env * 2.2);
        }
        /* charged: now and then a whole strand lights up along its length, and dies away again */
        if (en > 0.02) {
          for (i = 0; i < strands.length; i++) {
            o = strands[i];
            if (o.arc || o.vis < 0.02) continue;
            var sq = stime / o.surge.period + o.surge.phase; sq -= Math.floor(sq);
            var span = 1.7 / o.surge.period;
            if (sq > span) continue;
            var sx = sq / span, se = Math.pow(Math.sin(Math.PI * sx), 1.4) * (0.78 + 0.22 * Math.sin(stime * 23 + o.surge.phase * 50));
            var sa = 0.5 * se * Math.min(1, en) * fade * cfg.glow;
            var sg = ctx.createLinearGradient(o.X0, o.Y0, o.X3, o.Y3);
            sg.addColorStop(0, 'rgba(' + cfg.light + ',' + (sa * o.v0).toFixed(3) + ')');
            sg.addColorStop(1, 'rgba(' + cfg.light + ',' + (sa * Math.max(o.v1, 0.12 * o.v0)).toFixed(3) + ')');
            ctx.strokeStyle = sg; ctx.lineWidth = 1.25;
            ctx.beginPath(); ctx.moveTo(o.X0, o.Y0); ctx.bezierCurveTo(o.X1, o.Y1, o.X2, o.Y2, o.X3, o.Y3); ctx.stroke();
            nodes[o.from].flash = Math.max(nodes[o.from].flash, se * 1.4);
            if (o.to !== undefined) nodes[o.to].flash = Math.max(nodes[o.to].flash, se * 1.1);
          }
        }
        ctx.globalCompositeOperation = 'source-over';
      }

      /* the nodes */
      for (i = 0; i < nodes.length; i++) {
        n = nodes[i]; var v = Math.min(1.4, n.vis * fade * cfg.brightness * (1 + 0.5 * en)); if (v < 0.01) continue;
        var f = Math.min(1, n.flash + n.hi), sz;
        if (n.hi > 0.01) v = Math.max(v, n.hi);                          // … and is drawn in full, as a clear ringed dot
        if (n.kind === 'soft' && n.hi <= 0.01) {                                       // out of focus: a large faint disc
          sz = 15 * n.size; ctx.globalAlpha = Math.min(1, v * (0.16 + 0.25 * f)); ctx.drawImage(sprite, n.X - sz, n.Y - sz, sz * 2, sz * 2);
        } else {
          sz = (n.kind === 'ring' ? 13 : 8) * n.size * (1 + 0.5 * f) + 12 * n.hi;
          ctx.globalAlpha = Math.min(1, v * (0.34 + 0.6 * f)); ctx.drawImage(sprite, n.X - sz, n.Y - sz, sz * 2, sz * 2);
          if (n.kind === 'ring' || n.hi > 0.01) {
            ctx.globalAlpha = n.kind === 'ring' ? 1 : n.hi; ctx.strokeStyle = 'rgba(' + cfg.color + ',' + Math.min(1, v * (0.5 + 0.5 * f)).toFixed(3) + ')'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.arc(n.X, n.Y, 5.2 * n.size, 0, TAU); ctx.stroke();
          }
        }
        ctx.globalAlpha = 1;
      }

      /* keep the orb clear */
      if (hole > 0) {
        ctx.globalCompositeOperation = 'destination-out';
        var hg = ctx.createRadialGradient(fx, fy, hole * 0.96, fx, fy, hole * 1.1);
        hg.addColorStop(0, 'rgba(0,0,0,1)'); hg.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(fx, fy, hole * 1.1, 0, TAU); ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    /* ---------- running ---------- */
    var still = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var raf = 0, playing = true, visible = true, last = 0, dead = false;
    function tick(now) {
      raf = 0;
      if (!playing || !visible || dead) return;
      var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016; last = now;
      if (still) { fade = 1; draw(); return; }
      step(dt); if (fade < 1) fade = Math.min(1, fade + dt / 1.8);
      draw();
      raf = requestAnimationFrame(tick);
    }
    function step(dt) { var en = Math.max(0, Math.min(1.5, cfg.energy)); time += dt * cfg.speed; ptime += dt * cfg.pulse * (1 + 0.8 * en); stime += dt * en; }
    function wake() { if (!raf && playing && visible && !dead) { last = 0; raf = requestAnimationFrame(tick); } }

    var ro = global.ResizeObserver ? new ResizeObserver(function () { resize(); draw(); }) : null;
    if (ro) ro.observe(canvas);
    var io = global.IntersectionObserver ? new IntersectionObserver(function (en) {
      visible = en[en.length - 1].isIntersecting; wake();              // no work while off screen
    }) : null;
    if (io) io.observe(canvas);

    resize(); if (still) fade = 1;
    draw(); wake();

    return {
      play: function () { if (!playing) { playing = true; wake(); } },
      pause: function () { playing = false; },
      /* for testing and stills: move time on by hand and draw one frame */
      advance: function (seconds) { fade = 1; step(seconds); draw(); },
      /* change an option while it runs, e.g. set('energy', 0.6) — not nodes, colours or seed */
      set: function (name, value) { if (name in DEFAULTS) { cfg[name] = value; if (!playing || still) draw(); } },
      /* where the nodes are right now, in CSS pixels from the canvas's top-left corner, and how many lines meet at each (for labels placed on them) */
      nodes: function () { return nodes.map(function (n, i) { return { index: i, x: n.X, y: n.Y, visibility: n.vis, kind: n.kind, links: n.links || 0 }; }); },
      /* make one node glow steadily (amount 0…1), e.g. while a label sits on it */
      highlight: function (index, amount) { if (nodes[index]) nodes[index].hiTo = Math.max(0, Math.min(1, amount || 0)); },
      destroy: function () { dead = true; if (raf) cancelAnimationFrame(raf); if (ro) ro.disconnect(); if (io) io.disconnect(); }
    };
  }

  /* start every canvas marked data-neural-lines */
  function auto(root) {
    root = root && root.querySelectorAll ? root : document;
    var list = root.querySelectorAll('canvas[data-neural-lines]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i], opts = {}, key;
      if (el.__lines) continue;
      for (key in DEFAULTS) {
        var raw = el.dataset[key];
        if (raw === undefined) continue;
        opts[key] = typeof DEFAULTS[key] === 'number' ? parseFloat(raw) : raw;
      }
      el.__lines = NeuralLines(el, opts);
    }
  }

  global.NeuralLines = NeuralLines;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auto);
  else auto();
  document.addEventListener('shopify:section:load', function (e) { auto(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('canvas[data-neural-lines]').forEach(function (el) {
      if (el.__lines) { el.__lines.destroy(); delete el.__lines; }
    });
  });
})(window);
